import React, { useState, useRef } from 'react';
import {
  Box,
  VStack,
  Heading,
  Text,
  Container,
  AspectRatio,
  useColorModeValue,
  useBreakpointValue,
  Icon,
  IconButton,
  HStack,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
} from '@chakra-ui/react';
import {
  FaPlay,
  FaPause,
  FaVolumeUp,
  FaVolumeMute,
  FaExpand,
  FaCompress,
} from 'react-icons/fa';

import demo from "../../assets/piepos-demo-1.mp4"

const Demo = () => {
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const cardBgColor = useColorModeValue('white', 'gray.700');
  const videoRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);

  const headingSize = useBreakpointValue({ base: 'xl', md: '2xl' });

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleVolumeChange = (value) => {
    setVolume(value);
    if (videoRef.current) {
      videoRef.current.volume = value;
    }
  };

  const handleProgressChange = (value) => {
    if (videoRef.current) {
      videoRef.current.currentTime = value;
      setCurrentTime(value);
    }
  };

  const toggleFullscreen = () => {
    if (videoRef.current) {
      if (!document.fullscreenElement) {
        videoRef.current.parentElement.requestFullscreen();
        setIsFullscreen(true);
      } else {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      videoRef.current.volume = volume;
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <Box minH="100vh" position="relative" overflow="hidden" margin="10">
      {/* Mobile decorations */}
      <Box
        display={{ base: 'block', md: 'none' }}
        position="absolute"
        top="12%"
        right="8%"
        w="45px"
        h="45px"
        bg="purple.200"
        borderRadius="50%"
        opacity="0.6"
        zIndex="0"
        animation="pulse 3s ease-in-out infinite"
        sx={{
          '@keyframes pulse': {
            '0%, 100%': { transform: 'scale(1)' },
            '50%': { transform: 'scale(1.2)' }
          }
        }}
      />

      <Box
        display={{ base: 'block', md: 'none' }}
        position="absolute"
        bottom="18%"
        left="10%"
        w="35px"
        h="35px"
        bg="pink.200"
        borderRadius="50% 50% 0 50%"
        opacity="0.7"
        zIndex="0"
        animation="float 4s ease-in-out infinite"
        sx={{
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px)' },
            '50%': { transform: 'translateY(-10px)' }
          }
        }}
      />

      {/* Gradient Overlays */}
      <Box
        position="absolute"
        top="50%"
        left="50%"
        right="0"
        bottom="0"
        bgGradient="radial(circle at 30% 30%, rgba(168, 85, 247, 0.08) 0%, transparent 50%)"
        zIndex="0"
        pointerEvents="none"
      />

      <Container
        maxW="7xl"
        position="relative"
        zIndex="1"
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        py={{ base: 8, md: 0 }}
      >
        <VStack spacing={{ base: 6, md: 12 }} px={{ base: 2, md: 0 }} w="full">

          {/* Header Section */}
          <VStack spacing={{ base: 3, md: 6 }} textAlign="center" maxW="4xl">
            <Heading
              as="h2"
              size={headingSize}
              color={textColor}
              fontWeight="bold"
              lineHeight="1.2"
            >
              See PiePOS in Action
            </Heading>

            <Box position="relative">
              <Text
                fontSize={{ base: 'md', md: 'lg' }}
                color="gray.600"
                maxW="3xl"
                fontWeight="medium"
                lineHeight="1.6"
                px={{ base: 2, md: 0 }}
              >
                Watch how PiePOS{' '}
                <Box as="span" position="relative" display="inline-block">
                  transforms restaurant operations
                  {/* Doodle underline */}
                  <Box
                    position="absolute"
                    bottom={-1}
                    left="0"
                    w="100%"
                    h="3px"
                    bg="purple.400"
                    borderRadius="full"
                    transform="rotate(-1deg)"
                    opacity={0.8}
                  />
                </Box>{' '}
                with intuitive design and powerful features.
              </Text>
            </Box>
          </VStack>

          {/* Video Player Card */}
          <Box
            bg={cardBgColor}
            p={{ base: 2, md: 6 }}
            borderRadius="2xl"
            border="2px solid"
            borderColor="purple.200"
            maxW="5xl"
            w="full"
            position="relative"
            overflow="hidden"
            boxShadow="0 10px 30px rgba(0,0,0,0.1)"
          >
            {/* Card decoration */}
            <Box
              position="absolute"
              top="-60px"
              right="-60px"
              w="150px"
              h="150px"
              bg="purple.50"
              borderRadius="50%"
              opacity="0.5"
              zIndex="0"
            />

            <VStack spacing={{ base: 1, md: 6 }} position="relative" zIndex="1">
              <AspectRatio ratio={16 / 9} w="full">
                <Box
                  position="relative"
                  borderRadius="xl"
                  overflow="hidden"
                  bg="gray.900"
                  onMouseEnter={() => setShowControls(true)}
                  onMouseLeave={() => setShowControls(isPlaying ? false : true)}
                >
                  {/* Video Element */}
                  <video
                    ref={videoRef}
                    src={demo}
                    width="100%"
                    height="100%"
                    onTimeUpdate={handleTimeUpdate}
                    onLoadedMetadata={handleLoadedMetadata}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    onClick={togglePlayPause}
                  />

                  {/* Custom Video Controls Overlay */}
                  <Box
                    position="absolute"
                    bottom="0"
                    left="0"
                    right="0"
                    bg="linear-gradient(transparent, rgba(0,0,0,0.8))"
                    p={{ base: 2, md: 4 }}
                    opacity={showControls ? 1 : 0}
                    transition="opacity 0.3s ease"
                  >
                    {/* Progress Bar */}
                    <Box mb={{ base: 2, md: 3 }} display={{ base: "none", md:"block" }}>
                      <Slider
                        value={currentTime}
                        max={duration || 100}
                        onChange={handleProgressChange}
                        colorScheme="purple"
                        size={{ base: "sm", md: "md" }}
                      >
                        <SliderTrack bg="whiteAlpha.300">
                          <SliderFilledTrack />
                        </SliderTrack>
                        <SliderThumb boxSize={{ base: 3, md: 4 }} />
                      </Slider>
                    </Box>

                    {/* Control Buttons */}
                    <HStack
                      justify="space-between"
                      align="center"
                      spacing={{ base: 1, md: 3 }}
                    >
                      {/* Left Controls */}
                      <HStack spacing={{ base: 1, md: 2 }}>
                        <IconButton
                          aria-label={isPlaying ? "Pause" : "Play"}
                          icon={<Icon as={isPlaying ? FaPause : FaPlay} />}
                          onClick={togglePlayPause}
                          size={{ base: "sm", md: "md" }}
                          variant="ghost"
                          colorScheme="whiteAlpha"
                          color="white"
                          _hover={{ bg: "whiteAlpha.200" }}
                        />

                        <IconButton
                          aria-label={isMuted ? "Unmute" : "Mute"}
                          icon={<Icon as={isMuted ? FaVolumeMute : FaVolumeUp} />}
                          onClick={toggleMute}
                          size={{ base: "sm", md: "md" }}
                          variant="ghost"
                          colorScheme="whiteAlpha"
                          color="white"
                          _hover={{ bg: "whiteAlpha.200" }}
                        />

                        {/* Volume Slider - Hidden on mobile */}
                        <Box
                          w={{ base: "0", md: "100px" }}
                          display={{ base: "none", md: "block" }}
                        >
                          <Slider
                            value={volume}
                            max={1}
                            step={0.1}
                            onChange={handleVolumeChange}
                            colorScheme="purple"
                            size="sm"
                          >
                            <SliderTrack bg="whiteAlpha.300">
                              <SliderFilledTrack />
                            </SliderTrack>
                            <SliderThumb boxSize={3} />
                          </Slider>
                        </Box>

                        {/* Time Display */}
                        <Text
                          color="white"
                          fontSize={{ base: "xs", md: "sm" }}
                          fontFamily="mono"
                          minW={{ base: "60px", md: "80px" }}
                        >
                          {formatTime(currentTime)} / {formatTime(duration)}
                        </Text>
                      </HStack>

                      {/* Right Controls */}
                      <IconButton
                        aria-label={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                        icon={<Icon as={isFullscreen ? FaCompress : FaExpand} />}
                        onClick={toggleFullscreen}
                        size={{ base: "sm", md: "md" }}
                        variant="ghost"
                        colorScheme="whiteAlpha"
                        color="white"
                        _hover={{ bg: "whiteAlpha.200" }}
                      />
                    </HStack>
                  </Box>

                  {/* Play Button Overlay (shown when paused) */}
                  {!isPlaying && (
                    <Box
                      position="absolute"
                      top="50%"
                      left="50%"
                      transform="translate(-50%, -50%)"
                      zIndex="2"
                    >
                      <IconButton
                        aria-label="Play Video"
                        icon={<Icon as={FaPlay} fontSize={{ base: "2xl", md: "4xl" }} />}
                        onClick={togglePlayPause}
                        size={{ base: "lg", md: "xl" }}
                        borderRadius="full"
                        bg="gradient"
                        bgGradient="linear(45deg, purple.500, pink.500)"
                        color="white"
                        w={{ base: 16, md: 20 }}
                        h={{ base: 16, md: 20 }}
                        _hover={{
                          transform: 'scale(1.1)',
                          bgGradient: "linear(45deg, purple.600, pink.600)"
                        }}
                        transition="all 0.3s ease"
                        animation="playPulse 2s ease-in-out infinite"
                        sx={{
                          '@keyframes playPulse': {
                            '0%, 100%': { boxShadow: '0 0 0 0 rgba(168, 85, 247, 0.4)' },
                            '50%': { boxShadow: '0 0 0 20px rgba(168, 85, 247, 0)' }
                          }
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </AspectRatio>

              {/* Mobile time and Volume Control */}
              <Box
                display={{ base: "block", md: "none" }}
                w="full"
                px={4}
              >
                <HStack spacing={3} align="center">
                  <Icon as={FaPlay} color="gray.500" />
                  <Box flex="1" display={{ md: "none" }}>
                    <Slider
                      value={currentTime}
                      max={duration || 100}
                      onChange={handleProgressChange}
                      colorScheme="purple"
                      size={{ base: "sm", md: "md" }}
                    >
                      <SliderTrack bg="whiteAlpha.300">
                        <SliderFilledTrack />
                      </SliderTrack>
                      <SliderThumb boxSize={{ base: 3, md: 4 }} />
                    </Slider>
                  </Box>
                </HStack>

                <HStack spacing={3}>
                  <Icon as={FaVolumeUp} color="gray.500" />
                  <Slider
                    value={volume}
                    max={1}
                    step={0.1}
                    onChange={handleVolumeChange}
                    colorScheme="purple"
                    size="sm"
                    flex="1"
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb boxSize={4} />
                  </Slider>
                  <Text fontSize="sm" color="gray.500" minW="30px">
                    {Math.round(volume * 100)}%
                  </Text>
                </HStack>
              </Box>
            </VStack>
          </Box>

        </VStack>
      </Container>
    </Box>
  );
};

export default Demo;